// 控制台警告过滤已移至 index.html（确保在模块加载前执行）

import { createSSRApp } from 'vue';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import App from './App.vue';
import logger from './common/utils/logger';
import { ENV, isProd } from './common/config/env';
import i18n from './locales';
import { setupAuthInterceptor } from './common/auth';
import { installAppToast } from './common/utils/toast';
import { installAnnouncementModal } from './common/utils/announcementHost';
import { installSessionAuthPrompt } from './common/utils/sessionAuthHost';
import { installAppChrome } from './common/utils/appChromeHost';
import { logDeviceAndBridgeOnLoad } from './common/utils/nativeBridge';

// Buffer polyfill for browser
import { Buffer } from 'buffer';
if (typeof window !== 'undefined') {
  window.Buffer = Buffer;
  globalThis.Buffer = Buffer;
}

declare const __APP_VERSION__: string;
declare const __BUILD_TIME__: string;
declare const __APP_ENV__: string;

/** 开发期 Vue「enumerating keys」同文案只报一次，避免控制台风暴 */
let vueEnumKeysWarned = false;

// 版本信息：生产静默；非生产打印，且始终挂到 window 便于排查
if (typeof window !== 'undefined') {
  (window as any).__APP_INFO__ = {
    version: __APP_VERSION__,
    env: __APP_ENV__,
    buildTime: __BUILD_TIME__,
  };
}
if (__APP_ENV__ !== 'production') {
  console.log(`[App] v${__APP_VERSION__} | env: ${__APP_ENV__} | built: ${__BUILD_TIME__}`);
}

// 启动即打印设备环境 + 是否有 WVJB（便于 App 内调试）
logDeviceAndBridgeOnLoad();


export function createApp() {
  const app = createSSRApp(App);

  // Pinia store setup
  const pinia = createPinia();
  pinia.use(piniaPluginPersistedstate);
  app.use(pinia);

  // Use i18n instance
  app.use(i18n);

  // Setup auth interceptor
  setupAuthInterceptor();

  // 全局现代 Toast（劫持 uni.showToast；必须带 pinia）
  installAppToast(pinia);

  // 全局公告弹窗（H5 挂 body，全站可弹）
  installAnnouncementModal(pinia, i18n);

  // 登录过期确认弹框（H5 挂 body）
  installSessionAuthPrompt(pinia, i18n);

  // App 级 header/tabbar/登录（H5 挂 body；App.vue 模板无法挂自定义兄弟节点）
  installAppChrome(pinia, i18n);

  // Global error handler — 勿把 component instance 丢进 console（会触发 ownKeys 枚举告警风暴）
  app.config.errorHandler = (err, _instance, info) => {
    logger.error('全局错误:', err, info);
  };

  // Global warning handler — 只打字符串；把 instance 交给 console 会反复触发
  // “Avoid app logic that relies on enumerating keys on a component instance”
  app.config.warnHandler = (msg, _instance, trace) => {
    if (isProd) return;
    const text = String(msg || '');
    if (text.includes('enumerating keys on a component instance')) {
      if (vueEnumKeysWarned) return;
      vueEnumKeysWarned = true;
      logger.warn(
        '[Vue] enumerating component keys (logged once). Often Vue DevTools / deep traversal; not a runtime failure.'
      );
      return;
    }
    logger.warn('[Vue]', text, trace ? String(trace).slice(0, 300) : '');
  };

  // Log environment info on startup (non-production)
  if (!isProd) {
    logger.info('应用启动', {
      env: ENV,
      buildTime: __BUILD_TIME__,
      mode: import.meta.env.MODE,
    });
  }

  return {
    app,
    pinia,
  };
}
